<template>
	<!-- Under the preview, for the same reason as OverlayControl: you judge a
	-- watermark by looking at the picture, so the controls belong beside it
	-- rather than three groups down a settings panel. -->
	<div class="flex flex-col gap-2 w-full">
		<!-- Text or logo, not both. Two marks at once would need two sets of
		-- placement controls to solve a problem nobody has reported having. -->
		<div class="flex gap-1" role="tablist">
			<button v-for="m in ['text', 'logo']" :key="m" role="tab"
				@click.prevent="setMode(m)"
				:aria-selected="logo.mode === m ? 'true' : 'false'"
				class="mode-tab grow rounded-lg px-3 py-1.5 text-[13px] font-bold"
				:class="logo.mode === m ? 'is-on' : ''">
				{{ m === 'text' ? t('watermark.modeText') : t('watermark.modeLogo') }}
			</button>
		</div>

		<template v-if="logo.mode === 'text'">
			<input type="text" :maxlength="MAX_WATERMARK_CHARS"
				:value="uiStore.watermarkText"
				@input="uiStore.watermarkText = $event.target.value"
				@change="$emit('change')"
				@keyup.enter="$event.target.blur()"
				:placeholder="t('watermark.placeholder')"
				:aria-label="t('watermark.textLabel')"
				class="input input-sm w-full rounded-lg" />
		</template>

		<template v-else>
			<!-- Fetched through our own origin, never straight from the source:
			-- a cross-origin logo taints the canvas and the export throws, so
			-- the preview would look right and the download would fail. -->
			<div class="flex gap-2">
				<input type="url" :value="typed"
					@input="typed = $event.target.value"
					@keyup.enter="fetchLogo()"
					:placeholder="t('watermark.logoPlaceholder')"
					:aria-label="t('watermark.logoUrl')"
					class="input input-sm grow rounded-lg" />
				<button @click.prevent="fetchLogo()" :disabled="logo.loading"
					class="btn btn-sm rounded-lg">
					{{ logo.loading ? t('watermark.fetching') : t('watermark.fetch') }}
				</button>
			</div>
			<p v-if="logo.error" class="logo-error text-[12px]">{{ logo.error }}</p>
			<div v-if="hasLogo()" class="flex items-center gap-2">
				<img :src="logo.objectUrl" alt="" class="logo-thumb" />
				<span class="logo-note text-[12px] grow">{{ logo.width }} &times; {{ logo.height }}</span>
				<button @click.prevent="drop()" class="btn btn-xs rounded-lg">
					{{ t('watermark.logoRemove') }}
				</button>
			</div>
		</template>

		<div class="flex flex-wrap items-center gap-2">
			<select :value="uiStore.watermarkPosition"
				@change="uiStore.watermarkPosition = $event.target.value; $emit('change')"
				:aria-label="t('watermark.position')"
				class="select select-sm rounded-lg grow">
				<option v-for="p in WATERMARK_POSITIONS" :key="p.value" :value="p.value">
					{{ t('watermark.pos.' + p.key) }}
				</option>
			</select>
		</div>

		<div v-if="logo.mode === 'text'" class="flex flex-wrap items-center gap-2">
			<!-- Each option is shown in its own face, so the list is a sample
			-- rather than a set of names to imagine. -->
			<select :value="uiStore.watermarkFont"
				@change="uiStore.watermarkFont = $event.target.value; $emit('change')"
				:aria-label="t('watermark.font')"
				class="select select-sm rounded-lg grow"
				:style="{fontFamily: fontStack(uiStore.watermarkFont)}">
				<option v-for="f in WATERMARK_FONTS" :key="f.key" :value="f.key"
					:style="{fontFamily: f.stack}">{{ f.name }}</option>
			</select>
			<div class="flex items-center gap-2">
				<button v-for="c in WATERMARK_COLOURS" :key="c.hex"
					@click.prevent="pick(c.hex)"
					:title="t('watermark.colour', { name: t('colours.' + c.key) })"
					:aria-label="t('watermark.colour', { name: t('colours.' + c.key) })"
					:aria-pressed="uiStore.watermarkColor === c.hex ? 'true' : 'false'"
					class="h-6 w-6 rounded-full border-2 transition"
					:class="uiStore.watermarkColor === c.hex ? 'border-white scale-110' : 'border-white/40'"
					:style="{backgroundColor: c.hex}"></button>
			</div>
		</div>

		<!-- Both sliders move the store on `input`, which costs nothing, and
		-- only ask for the work on `change`, which fires once on release. -->
		<label class="flex items-center gap-3">
			<span class="w-12 shrink-0 text-[12px] slider-label">{{ t('watermark.size') }}</span>
			<input type="range" :min="sizeRange.min * 100" :max="sizeRange.max * 100" step="1"
				:value="Math.round(sizeRange.value * 100)"
				@input="setSize(Number($event.target.value) / 100)"
				@change="$emit('change')"
				class="range range-sm grow" :aria-label="t('watermark.size')" />
		</label>
		<label class="flex items-center gap-3">
			<span class="w-12 shrink-0 text-[12px] slider-label">{{ t('watermark.strength') }}</span>
			<input type="range" min="5" max="100" step="5"
				:value="Math.round(uiStore.watermarkOpacity * 100)"
				@input="uiStore.watermarkOpacity = Number($event.target.value) / 100"
				@change="$emit('change')"
				class="range range-sm grow" :aria-label="t('watermark.strength')" />
			<span class="w-11 shrink-0 text-right text-[13px] font-bold tabular-nums slider-value">
				{{ Math.round(uiStore.watermarkOpacity * 100) }}%
			</span>
		</label>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { WATERMARK_POSITIONS, WATERMARK_COLOURS, WATERMARK_FONTS, fontStack,
         MAX_WATERMARK_CHARS, MIN_WATERMARK_SIZE, MAX_WATERMARK_SIZE,
         MIN_LOGO_SIZE, MAX_LOGO_SIZE } from "../lib/watermark"
import { logo, hasLogo, clearLogo, loadLogoFromUrl } from "../lib/logo"
import { useUiElements } from "../store/uiElements"

const props = defineProps({ baseUrl: { type: String, required: true } })
const emit = defineEmits(['change'])
const uiStore = useUiElements()
const { t } = useI18n()
// The field is bound to this, and seeded from the logo so a fetched address is
// still there to edit. It was bound to logo.sourceUrl while the typing came
// here, which worked only because Vue leaves a prop alone when it has not
// changed -- one refactor away from the field emptying itself under somebody
// mid-sentence. One piece of state, in one place.
const typed = ref(logo.sourceUrl || "")

// A colour is one gesture, so it asks for the work straight away; the sliders
// are drags, which is why they wait for `change`.
function pick(hex) {
	uiStore.watermarkColor = hex
	emit('change')
}

function setMode(mode) {
	if (logo.mode === mode) return
	logo.mode = mode
	emit('change')
}

async function fetchLogo() {
	const url = (typed.value || logo.sourceUrl || "").trim()
	if (!url) return
	if (await loadLogoFromUrl(props.baseUrl, url)) emit('change')
}

function drop() {
	clearLogo()
	typed.value = ""
	emit('change')
}

// One slider, two meanings: a logo is judged as a shape and wants more of the
// picture than lettering does, so the ranges are not the same.
const sizeRange = computed(() => logo.mode === 'logo'
	? {min: MIN_LOGO_SIZE, max: MAX_LOGO_SIZE, value: logo.size}
	: {min: MIN_WATERMARK_SIZE, max: MAX_WATERMARK_SIZE, value: uiStore.watermarkSize})

function setSize(v) {
	if (logo.mode === 'logo') logo.size = v
	else uiStore.watermarkSize = v
}
</script>

<style scoped>
/* These controls sit BELOW the drop zone, on the page's own background, not on
 * the purple inside it. text-white was borrowed from OverlayControl without
 * checking that: measured in the light theme it came out at a contrast ratio
 * of 1.04 against rgb(251, 250, 253), which is white on white. The tokens
 * follow the theme, which is the whole point of palette.css. */
.slider-label { color: var(--ink-mute); }
.slider-value { color: var(--ink); }
.logo-note { color: var(--ink-mute); }
.logo-error { color: var(--ink-soft); }
.mode-tab { color: var(--ink-mute); border: 1px solid var(--ink-faint); }
.mode-tab.is-on { color: var(--ink); border-color: var(--ink-mute); font-weight: 800; }
.logo-thumb { height: 32px; width: auto; max-width: 96px; object-fit: contain; }
</style>
